import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { ArrowUpRight, DoorOpen, HandHeart, ArrowLeft, ArrowRight } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

export function useServiceCards() {
  const navigate = useNavigate();
  const { t, tl, lang } = useLang();
  const { data: allCategories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: settings = {} } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: order = [] } = useQuery({
    queryKey: ["services-order"],
    queryFn: async () => (await api.get("/services-order")).data.order,
  });

  const services = allCategories.filter((c) => c.is_service && c.active !== false && !c.hidden);
  const shul = settings.synagogue;
  const shulTitle = shul && (lang !== "en" && shul[`title_${lang}`] ? shul[`title_${lang}`] : shul.title);
  const shulText = shul && (lang !== "en" && shul[`text_${lang}`] ? shul[`text_${lang}`] : shul.text);

  const cards = [
    ...services.map((c) => ({
      key: c.id,
      title: tl(c, "title"),
      text: tl(c, "description"),
      image: resolveImg(c.image_url),
      eyebrow: t("services.serves"),
      onClick: () => navigate(c.link || `/programs/${c.slug}`),
      testid: `service-card-${c.slug}`,
      Icon: HandHeart,
    })),
    ...(shul ? [{
      key: "synagogue",
      title: shulTitle,
      text: shulText,
      image: resolveImg(shul.image_url),
      eyebrow: t("weekly.ourshul"),
      onClick: () => navigate("/classes"),
      testid: "service-card-synagogue",
      Icon: DoorOpen,
    }] : []),
  ];
  const orderIdx = (k) => { const i = (order || []).indexOf(k); return i === -1 ? 1e6 : i; };
  return [...cards].sort((a, b) => orderIdx(a.key) - orderIdx(b.key));
}

export function ServiceCard({ card }) {
  return (
    <motion.button
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      onClick={card.onClick}
      data-testid={card.testid}
      className="group relative w-full h-80 rounded-3xl overflow-hidden text-left shadow-[0_12px_40px_rgba(15,23,42,0.1)]"
    >
      {card.image ? (
        <img src={card.image} alt={card.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
      ) : <div className="absolute inset-0 bg-[#0F172A]" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/55 to-[#0F172A]/10" />
      <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
        <div className="flex items-start justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D8B45E]">
            <card.Icon size={14} /> {card.eyebrow}
          </span>
          <span className="shrink-0 w-9 h-9 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
            <ArrowUpRight size={16} />
          </span>
        </div>
        <h3 className="mt-3 font-serif text-2xl sm:text-[1.6rem] leading-tight">{card.title}</h3>
        <p className="mt-2 text-sm text-white/85 leading-relaxed line-clamp-3">{card.text}</p>
      </div>
    </motion.button>
  );
}

export function CommunityServices() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const cards = useServiceCards();
  const isRtl = lang === "he";
  const [emblaApi, setEmblaApi] = useState(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const orderKey = cards.map((c) => c.key).join("|");

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
      setSelected(emblaApi.selectedScrollSnap());
      setSnaps(emblaApi.scrollSnapList());
    };
    emblaApi.reInit();
    emblaApi.scrollTo(0, true);
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect); emblaApi.off("reInit", onSelect); };
  }, [emblaApi, orderKey]);

  if (cards.length === 0) return null;

  return (
    <section id="community-services" data-testid="community-services-section" className="relative z-10 py-24 sm:py-28 bg-[#F1EEE8] border-t border-[#0F172A]/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="services.eyebrow" as="span" /></span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight max-w-xl leading-tight"><EditableText k="services.title" as="span" /></h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col sm:items-end gap-4">
              <p className="max-w-sm text-[#475569]"><EditableText k="services.desc" as="span" /></p>
              <button data-testid="services-view-all" onClick={() => navigate("/services")}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#1e293b] transition-colors">
                <EditableText k="services.viewall" as="span" /> <ArrowUpRight size={15} />
              </button>
            </div>
          </Reveal>
        </div>

        <Carousel key={lang} setApi={setEmblaApi} opts={{ align: "start", loop: false, direction: isRtl ? "rtl" : "ltr" }} data-testid="services-slider">
          <CarouselContent className="-ml-5">
            {cards.map((card) => (
              <CarouselItem key={card.key} className="pl-5 basis-[82%] sm:basis-1/2 lg:basis-1/3">
                <ServiceCard card={card} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {cards.length > 1 && (
          <div className="mt-10 hidden sm:flex items-center justify-center gap-4" data-testid="services-nav">
            <button data-testid="services-prev" onClick={() => emblaApi?.scrollPrev()} disabled={!canPrev} aria-label="Previous"
              className="grid h-[52px] w-[52px] place-items-center rounded-full bg-[#F4EEE2] text-[#0F172A] shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-all hover:bg-white hover:text-[#BE923C] hover:shadow-[0_10px_24px_rgba(15,23,42,0.12)] disabled:opacity-30 disabled:hover:bg-[#F4EEE2] disabled:hover:text-[#0F172A] disabled:hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)]">
              <ArrowLeft size={20} />
            </button>
            <button data-testid="services-next" onClick={() => emblaApi?.scrollNext()} disabled={!canNext} aria-label="Next"
              className="grid h-[52px] w-[52px] place-items-center rounded-full bg-[#F4EEE2] text-[#0F172A] shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-all hover:bg-white hover:text-[#BE923C] hover:shadow-[0_10px_24px_rgba(15,23,42,0.12)] disabled:opacity-30 disabled:hover:bg-[#F4EEE2] disabled:hover:text-[#0F172A] disabled:hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)]">
              <ArrowRight size={20} />
            </button>
          </div>
        )}

        {snaps.length > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2 sm:hidden" data-testid="services-dots">
            {snaps.map((_, i) => (
              <button key={i} aria-label={`Go to slide ${i + 1}`} onClick={() => emblaApi?.scrollTo(i)}
                className={`h-2 rounded-full transition-all ${selected === i ? "w-6 bg-[#BE923C]" : "w-2 bg-[#0F172A]/20"}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
